import { Side } from '@/schemas/tiles';
import type { TileId } from '@/types/ids';

/**
 * Represents a single point on a tile where a connection can be made
 */
export interface ConnectionPoint {
  tileId: TileId;
  side: Side;
}

/**
 * Represents a connection between two tile sides
 */
export interface Connection {
  a: ConnectionPoint;
  b: ConnectionPoint;
}

/**
 * DTO for serializing/deserializing the connection graph
 */
export interface ConnectionGraphDto {
  connections: Connection[];
  /** Explicitly erased edges, including otherwise automatic connections. */
  disconnected?: Connection[];
}

/**
 * ConnectedNeighbor represents a tile connected to another tile via a specific side
 */
export interface ConnectedNeighbor {
  viaSide: Side;
  point: ConnectionPoint;
}

/**
 * ConnectionGraph is a simple data structure that stores connections between tiles.
 * It maintains a list of Connection objects, where each connection links two ConnectionPoints.
 * Uses multiple indexes for O(1) lookups by tile ID, connection point, and connection key.
 */
export class ConnectionGraph {
  private connections: Connection[] = [];
  private connectionIndex = new Map<string, Set<Connection>>();
  private tileConnectionIndex = new Map<TileId, Set<Connection>>();
  private connectionByKey = new Map<string, Connection>();
  private duplicatedKeys = new Set<string>();

  constructor(dto?: ConnectionGraphDto) {
    if (dto) {
      this.connections = [...dto.connections];
      this.rebuildIndex();
    }
  }

  /**
   * Get all connections in the graph
   */
  getAllConnections(): readonly Connection[] {
    return this.connections;
  }

  /**
   * Add a connection between two points
   */
  addConnection(a: ConnectionPoint, b: ConnectionPoint): void {
    const connection: Connection = { a, b };
    const key = this.makeConnectionKey(connection);

    if (this.connectionByKey.has(key)) {
      return;
    }

    this.connections.push(connection);
    this.indexConnection(connection, key);
  }

  /**
   * Remove a specific connection
   */
  removeConnection(connection: Connection): void {
    const key = this.makeConnectionKey(connection);
    const stored = this.connectionByKey.get(key);
    if (!stored) {
      return;
    }

    // Resolve the canonical edge once. Rebuilding keys for every preceding
    // edge dominates undoing long strokes on a populated circuit.
    const idx =
      this.duplicatedKeys.has(key) || this.makeConnectionKey(stored) !== key
        ? this.connections.findIndex((c) => this.makeConnectionKey(c) === key)
        : this.connections.indexOf(stored);
    if (idx === -1) {
      return;
    }

    const removed = this.connections[idx];
    this.connections.splice(idx, 1);
    this.deindexConnection(removed, key);
  }

  /**
   * Remove all connections involving a specific tile
   */
  removeConnectionsForTile(tileId: TileId): void {
    this.removeConnectionsForTiles([tileId]);
  }

  /** Remove a batch with one ordered-array compaction, maintaining all indexes. */
  removeConnectionsForTiles(tileIds: Iterable<TileId>): void {
    const toRemove = new Set<Connection>();
    for (const tileId of tileIds) {
      for (const connection of this.tileConnectionIndex.get(tileId) ?? []) {
        toRemove.add(connection);
      }
    }
    if (toRemove.size === 0) return;
    this.connections = this.connections.filter((connection) => !toRemove.has(connection));
    for (const connection of toRemove) this.deindexConnection(connection);
  }

  /** Release empty lookup entries after an endpoint leaves its owning model. */
  forgetUnusedTileIndexes(tileId: TileId): void {
    const tileConnections = this.tileConnectionIndex.get(tileId);
    if (!tileConnections || tileConnections.size !== 0) return;
    this.tileConnectionIndex.delete(tileId);
    for (const side of Object.values(Side)) {
      const key = this.makePointKey({ tileId, side });
      if (this.connectionIndex.get(key)?.size === 0) this.connectionIndex.delete(key);
    }
  }

  /**
   * Remove all connections
   */
  clear(): void {
    this.connections = [];
    this.connectionIndex.clear();
    this.tileConnectionIndex.clear();
    this.connectionByKey.clear();
    this.duplicatedKeys.clear();
  }

  /**
   * Get all connections for a specific tile (uses tile index for O(k) lookup)
   */
  getConnectionsForTile(tileId: TileId): Connection[] {
    const tileConns = this.tileConnectionIndex.get(tileId);
    return tileConns ? Array.from(tileConns) : [];
  }

  /**
   * Get the connection point on the other side of a connection
   */
  getConnectedPoint(point: ConnectionPoint): ConnectionPoint | undefined {
    const connections = this.getConnectionsAt(point);
    if (connections.length === 0) {
      return undefined;
    }

    const connection = connections[0];
    if (this.pointsEqual(connection.a, point)) {
      return connection.b;
    }
    return connection.a;
  }

  /**
   * Get all connections at a specific connection point (uses point index for O(1) lookup)
   */
  getConnectionsAt(point: ConnectionPoint): Connection[] {
    const key = this.makePointKey(point);
    const indexed = this.connectionIndex.get(key);
    return indexed ? Array.from(indexed) : [];
  }

  /**
   * Get connection flags for a tile (uses tile index for O(k) lookup)
   */
  getConnectionFlags(tileId: TileId): Record<Side, boolean> {
    const flags: Record<Side, boolean> = {
      [Side.TOP]: false,
      [Side.RIGHT]: false,
      [Side.BOTTOM]: false,
      [Side.LEFT]: false,
    };

    const tileConns = this.tileConnectionIndex.get(tileId);
    if (!tileConns) {
      return flags;
    }

    for (const connection of tileConns) {
      if (connection.a.tileId === tileId) {
        flags[connection.a.side] = true;
      }
      if (connection.b.tileId === tileId) {
        flags[connection.b.side] = true;
      }
    }

    return flags;
  }

  /**
   * Get all neighbors connected to a tile (uses tile index for O(k) lookup)
   */
  getConnectedNeighbors(tileId: TileId): ConnectedNeighbor[] {
    const tileConns = this.tileConnectionIndex.get(tileId);
    if (!tileConns) {
      return [];
    }

    const neighbors: ConnectedNeighbor[] = [];

    for (const connection of tileConns) {
      if (connection.a.tileId === tileId) {
        neighbors.push({
          viaSide: connection.a.side,
          point: connection.b,
        });
      } else if (connection.b.tileId === tileId) {
        neighbors.push({
          viaSide: connection.b.side,
          point: connection.a,
        });
      }
    }

    return neighbors;
  }

  /**
   * Serialize to DTO
   */
  toDto(): ConnectionGraphDto {
    return {
      connections: [...this.connections],
    };
  }

  /**
   * Create a unique key for a connection (order-independent)
   */
  private makeConnectionKey(connection: Connection): string {
    const keyA = this.makePointKey(connection.a);
    const keyB = this.makePointKey(connection.b);

    return keyA < keyB ? `${keyA}|${keyB}` : `${keyB}|${keyA}`;
  }

  /**
   * Create a unique key for a connection point
   */
  private makePointKey(point: ConnectionPoint): string {
    return `${point.tileId}:${point.side}`;
  }

  /**
   * Check if two points are equal
   */
  private pointsEqual(a: ConnectionPoint, b: ConnectionPoint): boolean {
    return a.tileId === b.tileId && a.side === b.side;
  }

  /**
   * Index a connection into all lookup structures
   */
  private indexConnection(connection: Connection, key?: string): void {
    const keyA = this.makePointKey(connection.a);
    const keyB = this.makePointKey(connection.b);
    const connKey = key ?? this.makeConnectionKey(connection);

    if (!this.connectionIndex.has(keyA)) {
      this.connectionIndex.set(keyA, new Set());
    }
    if (!this.connectionIndex.has(keyB)) {
      this.connectionIndex.set(keyB, new Set());
    }
    this.connectionIndex.get(keyA)!.add(connection);
    this.connectionIndex.get(keyB)!.add(connection);

    const tileIdA = connection.a.tileId;
    const tileIdB = connection.b.tileId;
    if (!this.tileConnectionIndex.has(tileIdA)) {
      this.tileConnectionIndex.set(tileIdA, new Set());
    }
    if (!this.tileConnectionIndex.has(tileIdB)) {
      this.tileConnectionIndex.set(tileIdB, new Set());
    }
    this.tileConnectionIndex.get(tileIdA)!.add(connection);
    this.tileConnectionIndex.get(tileIdB)!.add(connection);

    // Saved DTOs can contain duplicate keys. The previous ordered search
    // removed the first stored occurrence, so retain that canonical edge.
    if (this.connectionByKey.has(connKey)) this.duplicatedKeys.add(connKey);
    else this.connectionByKey.set(connKey, connection);
  }

  /**
   * Remove a connection from all lookup structures
   */
  private deindexConnection(connection: Connection, key?: string): void {
    const keyA = this.makePointKey(connection.a);
    const keyB = this.makePointKey(connection.b);
    const connKey = key ?? this.makeConnectionKey(connection);

    this.connectionIndex.get(keyA)?.delete(connection);
    this.connectionIndex.get(keyB)?.delete(connection);
    this.tileConnectionIndex.get(connection.a.tileId)?.delete(connection);
    this.tileConnectionIndex.get(connection.b.tileId)?.delete(connection);
    this.connectionByKey.delete(connKey);
  }

  /**
   * Rebuild all indexes from scratch
   */
  private rebuildIndex(): void {
    this.connectionIndex.clear();
    this.tileConnectionIndex.clear();
    this.connectionByKey.clear();
    this.duplicatedKeys.clear();
    for (const connection of this.connections) {
      this.indexConnection(connection);
    }
  }

  get dto(): ConnectionGraphDto {
    return { connections: [...this.connections] };
  }
}
